import { useNavigate, useRouter } from '@tanstack/react-router';
import arrowDownwardsIcon from 'assets/icons/arrow-downwards-icon.svg';
import BackButton from 'components/BackButton/BackButton';
import { Button } from 'components/Button/Button';
import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';
import { CryptoDepositProductDto } from 'services/generated';
import { useCreateCryptoDepositAddressMutation } from 'store/slices/deposits-api';
import { EVENTS, trackEvent, TRANSACTION_TYPE, transactionEventFields } from 'utils/analytics';

type CryptoDepositDisclaimerViewProps = {
  productData: CryptoDepositProductDto | undefined;
  depositDisclaimerMessages: any;
};

const CryptoDepositDisclaimerView = ({
  productData,
  depositDisclaimerMessages,
}: CryptoDepositDisclaimerViewProps) => {
  const navigate = useNavigate();
  const router = useRouter();
  const [createCryptoDeposit, createCryptoDepositResult] =
    useCreateCryptoDepositAddressMutation();

  const goToCryptoCode = async () => {
    if (!productData?.pendingOrderId) {
      const result = await createCryptoDeposit(productData?.code!);
      if (result.error) return;

      // TRACK CRYPTO DEPOSIT CREATED
      trackEvent(EVENTS.CRYPTO_DEPOSIT_CREATED, {
        [transactionEventFields.currencyCode]: productData?.code!,
        [transactionEventFields.transactionType]: TRANSACTION_TYPE.DEPOSIT,
      });
    }
    navigate({ to: `/funds/deposit/crypto/${productData?.code!}/address` });
  };

  return (
    <div className="static lg:relative w-full mb-10">
      <BackButton className={'w-full text-left lg:bg-body-background mt-1 ml-0 pl-6 lg:-mt-4 pt-2 lg:ml-0 lg:pl-0 lg:pt-2'} onClick={() => router.history.back()} />

      <div className="flex flex-row justify-start -mb-4 sm:justify-center lg:mb-0 lg:pt-16 xl:pt-16">
        <div className="flex flex-col text-left md:text-left lg:text-center xl:text-center">
          <div>
            <h3 className="font-semibold text-3xl leading-9 tracking-wide pb-4">
              {depositDisclaimerMessages.disclaimerHeading} {productData?.name}
            </h3>
          </div>

          <div className="font-normal text-lg leading-6 tracking-wide text-body-text pb-10">
            {depositDisclaimerMessages.disclaimerSubHeading}
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-start rounded-lg lg:justify-center xl:justify-center">
        <div className="flex flex-col space-y-4 w-full sm:w-full md:w-full lg:w-1/2 xl:w-1/2">
          <div className="flex flex-col justify-center items-center rounded-lg bg-background p-6">
            <div className="m-6 relative">
              <CurrencyIcon isLarge currencyCode={productData?.code} />
            </div>
            <div className="relative left-6 bottom-12 z-2">
              <img src={arrowDownwardsIcon} alt="arrow-downwards-icon" />
            </div>
            <div className="w-full m-4">
              <div className="mb-2 text-sm">
                {depositDisclaimerMessages.networkHeading}
              </div>
              <div className="bg-input-background text-lg rounded-lg w-full justify-center p-3">
                {productData?.network}
              </div>
            </div>
            <div
              className="mt-4 mb-8"
              dangerouslySetInnerHTML={{
                __html: productData?.disclaimerCopy ?? '',
              }}
            />
          </div>

          <Button
            main
            full
            className="mt-1"
            loading={createCryptoDepositResult.isLoading}
            onClick={goToCryptoCode}>
            {depositDisclaimerMessages.buttonCopy}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CryptoDepositDisclaimerView;
